/**
 * 전역 WebSocket 연결 관리 Composable
 * 애플리케이션 전체에서 하나의 WebSocket 연결만 유지
 * 각 화면에서는 채널 구독/해제만 관리
 */

import { getWebSocketService } from '@/services/websocket';
import { useSymbolData } from './useSymbolData';
import { ref, readonly } from 'vue';

// 전역 상태
const isGlobalWebSocketInitialized = ref(false);
const globalWebSocketService = getWebSocketService();
let initializePromise: Promise<void> | null = null;

export function useGlobalWebSocket() {
  /**
   * 전역 WebSocket 연결 초기화 (한 번만 실행)
   */
  const initializeGlobalWebSocket = async () => {
    if (isGlobalWebSocketInitialized.value) {
      return;
    }

    if (initializePromise) {
      return initializePromise;
    }

    initializePromise = (async () => {
      try {
        const { loadAllSymbols } = useSymbolData();
        await loadAllSymbols();

        await globalWebSocketService.connect();

        isGlobalWebSocketInitialized.value = true;
      } catch (error) {
        console.error('[useGlobalWebSocket] 전역 WebSocket 연결 실패:', error);
        throw error;
      } finally {
        initializePromise = null;
      }
    })();

    return initializePromise;
  };

  /**
   * WebSocket 연결 상태 확인
   */
  const isConnected = () => {
    return globalWebSocketService.isConnected();
  };

  /**
   * 전역 WebSocket 연결 해제
   * @param allowAutoReconnect - 자동 재연결 허용 여부 (기본값: true, 자동 재연결 허용)
   *                             allowAutoReconnect=true인 경우, ReconnectManager에서 Access Token을 확인하여 재연결 여부를 결정합니다.
   */
  const disconnectGlobalWebSocket = async (allowAutoReconnect: boolean = true) => {
    if (!isGlobalWebSocketInitialized.value) {
      return;
    }

    try {
      await globalWebSocketService.disconnect(allowAutoReconnect);
      isGlobalWebSocketInitialized.value = false;
    } catch (error) {
      console.error('[useGlobalWebSocket] 전역 WebSocket 연결 해제 실패:', error);
    }
  };

  return {
    // 상태
    isGlobalWebSocketInitialized: readonly(isGlobalWebSocketInitialized),

    // 함수들
    initializeGlobalWebSocket,
    isConnected,
    disconnectGlobalWebSocket,
    getWebSocketService: () => globalWebSocketService,
  };
}
